import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/**
 * ModernSelect - A custom dropdown component with modern SaaS styling.
 * Extracted from ProfilePage for reuse across the application.
 */
const ModernSelect = ({ value, options, onChange, placeholder, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => (opt.value !== undefined ? opt.value : opt) === value);
    // Ensure displayLabel is always a string, never an object
    const displayLabel = selectedOption
        ? (selectedOption.label !== undefined ? selectedOption.label : selectedOption)
        : (placeholder || value || '');

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-full hover:border-blue-300 focus:ring-4 focus:ring-blue-100 transition-all active:scale-[0.98] shadow-sm"
            >
                <span className="truncate">{displayLabel}</span>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-300/30 z-50 overflow-hidden"
                    >
                        <div className="max-h-60 overflow-y-auto py-2">
                            {options.map((opt, i) => {
                                const val = opt.value !== undefined ? opt.value : opt;
                                // Ensure label is always a string
                                const label = opt.label !== undefined ? opt.label : (typeof opt === 'string' ? opt : String(opt));
                                const isSelected = val === value;
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => {
                                            onChange(val);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-all border-b border-slate-50 last:border-0
                                            ${isSelected
                                                ? 'bg-blue-50 text-blue-600 font-bold'
                                                : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                                            }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ModernSelect;
